import { useState, useEffect, useRef } from 'react';
import { getWsUrl } from '../api/sacada';

const STALE_AFTER_MS = 2000;

// ─────────────────────────────────────────────────────────────
// useWebSocket(app) — ouvre /ws/live/{app} et retourne les
// dernières données de CETTE application uniquement.
//
// Utilisation dans un composant :
//   const { data, connected } = useWebSocket('barriere');
//   → data.capteur_entree  (true/false)
//   → connected            (true si connecté au backend)
//
// La connexion s'ouvre au montage du composant et se ferme
// automatiquement au démontage (= quand on quitte la page).
// ─────────────────────────────────────────────────────────────

export default function useWebSocket(app, enabled = true) {
  const [data, setData]           = useState(null);
  const [lastMessage, setLastMessage] = useState(null);
  const [connected, setConnected] = useState(false);
  const [stale, setStale] = useState(true);
  const [error, setError] = useState(null);
  const [lastDataAt, setLastDataAt] = useState(null);
  const wsRef                     = useRef(null);
  const lastLiveReceivedAtRef     = useRef(null);

  useEffect(() => {
    if (!app || !enabled) {
      setData(null);
      setLastMessage(null);
      setConnected(false);
      setStale(true);
      setError(null);
      setLastDataAt(null);
      lastLiveReceivedAtRef.current = null;
      return;
    }

    let stopped = false;
    let reconnectTimer = null;

    const staleTimer = setInterval(() => {
      const receivedAt = lastLiveReceivedAtRef.current;
      if (receivedAt === null || Date.now() - receivedAt > STALE_AFTER_MS) {
        setStale(true);
      }
    }, 500);

    function connect() {
      const ws = new WebSocket(getWsUrl(app));
      wsRef.current = ws;

      ws.onopen = () => {
        console.log(`[WS] Connecté à '${app}'`);
        setConnected(true);
        setError(null);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'live_data') {
            setLastMessage(msg);
            setData(msg.data);   // → { capteur_entree, led_rouge, ... } selon l'app
            lastLiveReceivedAtRef.current = Date.now();
            setLastDataAt(msg.timestamp || new Date().toISOString());
            setStale(msg.stale === true);
            setError(null);
          } else if (msg.type === 'error') {
            setLastMessage(msg);
            setStale(true);
            setError(msg.message || 'Lecture PLC indisponible.');
          }
        } catch (e) {
          console.error('[WS] Erreur parsing JSON', e);
        }
      };

      ws.onclose = () => {
        setConnected(false);
        setStale(true);
        if (!stopped) {
          console.log(`[WS] Déconnecté de '${app}' — reconnexion dans 2s`);
          reconnectTimer = setTimeout(connect, 2000);
        }
      };

      ws.onerror = (err) => {
        console.error('[WS] Erreur', err);
        ws.close();
      };
    }

    connect();

    // Nettoyage : fermer le WS quand le composant est démonté
    // ou quand on change d'application (changement de page)
    return () => {
      stopped = true;
      clearInterval(staleTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (wsRef.current) wsRef.current.close();
    };
  }, [app, enabled]);

  return { data, connected, stale, error, lastDataAt, lastMessage };
}
